import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { erroNasDatas } from "@nhomi/core";
import { useSession } from "@/lib/auth";
import { dayKey } from "@/data/plates";
import { interacaoDe, OPCOES_DE_INTERACAO, type Interacao } from "@nhomi/core";
import { updateChallenge, watchChallenge, type Challenge } from "@/data/challenges";
import { capturar, EXTENSAO, prepararCapa, type FotoEscolhida } from "@/lib/image";
import { subir } from "@/lib/upload";
import { StoragePhoto } from "@/ui/StoragePhoto";
import { Empty, Spinner } from "@/ui/primitives";
import { IconBack, IconChevron, IconImage, IconMap, IconPencil, IconTarget } from "@/ui/icons";
import { descreverFalha, FalhaCard, type Falha } from "@/ui/Falha";

export function EditChallenge() {
  const { cid = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useSession();

  const [challenge, setChallenge] = useState<Challenge | null | undefined>(undefined);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [startDay, setStartDay] = useState("");
  const [endDay, setEndDay] = useState("");
  const [interacao, setInteracao] = useState<Interacao>("todos");
  const [capa, setCapa] = useState<FotoEscolhida | null>(null);
  const [capaPreview, setCapaPreview] = useState("");
  const arquivo = useRef<HTMLInputElement>(null);
  const [salvando, setSalvando] = useState(false);
  const [falha, setFalha] = useState<Falha | null>(null);
  const [carregado, setCarregado] = useState(false);

  useEffect(() => watchChallenge(cid, setChallenge), [cid]);

  /* Os campos são preenchidos UMA vez, na primeira chegada dos dados. A
     escuta continua ativa, e reescrever o estado a cada snapshot apagaria
     o que a pessoa está digitando — inclusive por causa da própria
     escrita dela. */
  useEffect(() => {
    if (!challenge || carregado) return;
    setName(challenge.name);
    setDescription(challenge.description ?? "");
    setStartDay(challenge.startDay ?? "");
    setEndDay(challenge.endDay ?? "");
    setInteracao(interacaoDe(challenge));
    setCarregado(true);
  }, [challenge, carregado]);

  /* A prévia sai dos bytes já copiados, e não do `File`: um object URL
     sobre o `File` da galeria do Android lê o `content://` só na hora de
     desenhar, e é esse ponteiro que morre. */
  useEffect(() => {
    if (!capa) { setCapaPreview(""); return; }
    let vivo = true;
    let url = "";
    void capa.bytes
      .then((bytes) => {
        if (!vivo) return;
        url = URL.createObjectURL(bytes);
        setCapaPreview(url);
      })
      .catch(() => { if (vivo) setCapaPreview(""); });
    return () => { vivo = false; if (url) URL.revokeObjectURL(url); };
  }, [capa]);

  if (challenge === undefined) return <div className="screen center"><Spinner /></div>;
  if (!challenge) {
    return <main className="screen"><Empty emoji="🤔" title="Desafio não encontrado" /></main>;
  }

  const souDono = challenge.ownerUid === user?.uid;
  if (!souDono) {
    return (
      <main className="screen">
        <Cabecalho cid={cid} />
        <Empty emoji="🔒" title="Só o dono do desafio edita isto">
          <p>Peça pra quem criou o desafio.</p>
        </Empty>
      </main>
    );
  }

  async function salvar() {
    const erroDeData = erroNasDatas({ startDay, endDay }, dayKey(), {
      startDay: challenge!.startDay ?? "", endDay: challenge!.endDay ?? "",
    });
    if (erroDeData) {
      setFalha({ codigo: "datas", etapa: "salvar o desafio", recado: erroDeData });
      return;
    }
    setSalvando(true);
    setFalha(null);
    let etapa = "salvar o desafio";
    try {
      let coverPath = challenge!.coverPath ?? null;

      if (capa) {
        etapa = "enviar a capa";
        /* O caminho leva a hora no nome em vez de ser fixo. Com nome fixo,
           trocar a capa não muda a URL — e o navegador de todo mundo
           continua servindo a antiga do cache por um ano, porque é isso
           que o `cacheControl: immutable` do upload manda ele fazer. */
        coverPath = `challenges/${cid}/covers/${user!.uid}/${Date.now()}.${EXTENSAO}`;
        await subir(coverPath, await prepararCapa(capa));
      }

      etapa = "salvar o desafio";
      await updateChallenge(cid, {
        name: name.trim() || challenge!.name,
        description: description.trim(),
        startDay,
        endDay,
        coverPath,
        interacao,
      });
      navigate(`/c/${cid}/detalhes`);
    } catch (err) {
      setSalvando(false);
      setFalha(descreverFalha(err, etapa));
    }
  }

  return (
    <main className="screen">
      <Cabecalho cid={cid} acao={
        <button className="topbar-action" onClick={salvar} disabled={salvando}>
          {salvando ? "Salvando…" : "Salvar"}
        </button>
      } />

      <div className="screen-body no-tabbar">
        {falha && <FalhaCard falha={falha} />}

        <div className="compose-head capa">
          <div className="compose-photo capa">
            {capaPreview
              ? <img src={capaPreview} alt="Nova capa" />
              : challenge.coverPath
                ? <StoragePhoto path={challenge.coverPath} alt="Capa do desafio" ratio="16 / 9" />
                : <div className="compose-photo-vazia"><IconImage size={30} /></div>}
            <button className="edit-btn" onClick={() => arquivo.current?.click()}
              aria-label="Trocar a capa">
              <IconPencil size={20} />
            </button>
          </div>
        </div>
        <div className="hint-row center">Toque no lápis pra trocar a capa</div>
        <input ref={arquivo} type="file" accept="image/*" hidden
          onChange={(e) => {
            // Copia os bytes antes de soltar o input: o `File` do
            // Android perde o conteúdo e guarda os metadados.
            const f = e.target.files?.[0];
            const escolhida = f ? capturar(f) : null;
            e.target.value = "";
            if (escolhida) setCapa(escolhida);
          }} />

        <div className="card">
          <label className="field">
            <span className="field-label">Nome</span>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
          </label>
          <label className="field">
            <span className="field-label">Regras / descrição</span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
              maxLength={400} />
          </label>
        </div>

        <div className="row-2">
          <div className="card"><label className="field">
            <span className="field-label">Começa</span>
            <input type="date" value={startDay} onChange={(e) => setStartDay(e.target.value)} />
          </label></div>
          <div className="card"><label className="field">
            <span className="field-label">Termina <i className="opcional">opcional</i></span>
            <input type="date" value={endDay} onChange={(e) => setEndDay(e.target.value)} />
          </label></div>
        </div>

        {/* Quem pode comentar e dar nota. Palpites e reações ficam sempre:
            são o jogo, não a conversa. Com "Ninguém", os comentários
            antigos ficam escondidos, não apagados. */}
        <div className="section-label left">Quem pode comentar e dar nota</div>
        <div className="card list-card">
          {OPCOES_DE_INTERACAO.map((o) => (
            <button key={o.id} className="list-row" role="radio" aria-checked={interacao === o.id}
              onClick={() => setInteracao(o.id)}>
              <span className="ico emoji">{interacao === o.id ? "🔘" : "⚪"}</span>
              <span className="label">{o.rotulo}<span className="pref-hint">{o.dica}</span></span>
            </button>
          ))}
        </div>
        {interacao === "ninguem" && (
          <div className="hint-row">
            Somem também o ranking de melhores pratos e as missões de nota e de comentário.
          </div>
        )}

        <div className="card list-card">
          <Link className="list-row" to={`/c/${cid}/regras`}>
            <span className="ico"><IconTarget size={22} /></span>
            <span className="label">
              Pontos, refeições e selos
              <span className="pref-hint">
                quanto vale cada refeição, selos do passaporte e eventos
              </span>
            </span>
            <span className="chev"><IconChevron size={18} /></span>
          </Link>
          <Link className="list-row" to={`/c/${cid}/guia`}>
            <span className="ico"><IconMap size={22} /></span>
            <span className="label">Nosso mapa</span>
            <span className="chev"><IconChevron size={18} /></span>
          </Link>
        </div>

        <div className="hint-row">
          O placar é refeito sozinho sempre que a pontuação muda — não existe
          botão de recalcular porque não existe placar que possa ficar velho.
        </div>

        <div className="gap" />
      </div>
    </main>
  );
}

function Cabecalho({ cid, acao }: { cid: string; acao?: React.ReactNode }) {
  return (
    <div className="topbar">
      <Link className="topbar-btn" to={`/c/${cid}/detalhes`} aria-label="Voltar"><IconBack /></Link>
      <div className="topbar-title">Editar desafio</div>
      {acao ?? <span className="topbar-btn" />}
    </div>
  );
}
